import { type Block, type EnvelopeColumn, type PageEntry, type Row, isColumn, isSection, isTabs } from './types';

export type BoardOperation =
  | { op: 'add'; block: Record<string, unknown>; after?: string; before?: string; row?: 'new' | 'same' }
  | { op: 'set'; id: string; patch: Record<string, unknown> }
  | { op: 'move'; id: string; before?: string; after?: string; row?: 'new' | 'same'; tab?: string }
  | { op: 'form'; id: string; kind: string }
  | { op: 'remove'; id: string; keep?: boolean }
  | { op: 'section'; title: string; contents?: Array<string>; lead?: string }
  | { op: 'tab'; title: string; after?: string; before?: string }
  | { op: 'measure'; id: string; def: Record<string, unknown> | null }
  | { op: 'dataset'; id: string; def: Record<string, unknown> | null }
  | { op: 'filter'; column: string; def: Record<string, unknown> | null }
  | { op: 'param' | 'query'; id: string; def: Record<string, unknown> | null }
  | { op: 'view'; id: string; html?: string; css?: string; js?: string };

export type Direction = 'up' | 'down' | 'left' | 'right';

export type DropSide = 'before' | 'after' | 'above' | 'below';

export const SPANS: Array<[number, string]> = [
  [3, 'Quarter'],
  [4, 'Third'],
  [6, 'Half'],
  [8, 'Two thirds'],
  [12, 'Full width'],
];

export const FORMS: Array<[string, string]> = [
  ['kpis', 'Figures'],
  ['line', 'Line'],
  ['area', 'Area'],
  ['calendar', 'Calendar'],
  ['column', 'Columns'],
  ['bar', 'Ranked bars'],
  ['pie', 'Share'],
  ['table', 'Table'],
];

export function suitableForms(current: string, columns: Array<EnvelopeColumn>): Array<[string, string]> {
  const measures = columns.filter((column) => column.role === 'measure').length;
  const fitting = columns.some((column) => column.role === 'time')
    ? ['line', 'area', 'column', ...(measures === 1 && !columns.some((column) => column.role === 'dim') ? ['calendar'] : []), 'table']
    : columns.some((column) => column.role === 'dim')
      ? ['bar', ...(measures === 1 ? ['pie'] : []), 'table']
      : ['kpis', 'table'];
  return FORMS.filter(([form]) => form === current || fitting.includes(form));
}

function rowsOf(page: Array<PageEntry>): Array<Row> {
  return page.flatMap((entry) =>
    isSection(entry) ? entry.rows : isTabs(entry) ? entry.tabs.flatMap((tab) => rowsOf(tab.page)) : [entry],
  );
}

const idsOf = (row: Row): Array<string> =>
  row.flatMap((item) => (isColumn(item) ? item.map((block: Block) => block.id) : [item.id]));

export function pageBlocks(page: Array<PageEntry>): Array<Block> {
  return rowsOf(page).flatMap((row) => row.flatMap((item) => (isColumn(item) ? item : [item])));
}

export function findBlock(page: Array<PageEntry>, id: string | undefined): Block | undefined {
  if (!id) return undefined;
  for (const row of rowsOf(page))
    for (const item of row) for (const block of isColumn(item) ? item : [item]) if (block.id === id) return block;
  return undefined;
}

export function moveOperation(page: Array<PageEntry>, id: string, direction: Direction): BoardOperation | undefined {
  const rows = rowsOf(page).map(idsOf);
  const rowIndex = rows.findIndex((ids) => ids.includes(id));
  if (rowIndex < 0) return undefined;
  const row = rows[rowIndex];
  const at = row.indexOf(id);
  if (direction === 'left') return at > 0 ? { op: 'move', id, before: row[at - 1], row: 'same' } : undefined;
  if (direction === 'right')
    return at < row.length - 1 ? { op: 'move', id, after: row[at + 1], row: 'same' } : undefined;
  if (direction === 'up') {
    if (row.length > 1) return { op: 'move', id, before: row.find((other) => other !== id), row: 'new' };
    const previous = rows[rowIndex - 1];
    return previous ? { op: 'move', id, before: previous[0], row: 'new' } : undefined;
  }
  if (row.length > 1) return { op: 'move', id, after: [...row].reverse().find((other) => other !== id), row: 'new' };
  const next = rows[rowIndex + 1];
  return next ? { op: 'move', id, after: next[0], row: 'new' } : undefined;
}

export function dropOperation(id: string, target: string, side: DropSide): BoardOperation | undefined {
  if (id === target) return undefined;
  if (side === 'before') return { op: 'move', id, before: target, row: 'same' };
  if (side === 'after') return { op: 'move', id, after: target, row: 'same' };
  return side === 'above'
    ? { op: 'move', id, before: target, row: 'new' }
    : { op: 'move', id, after: target, row: 'new' };
}

export function dropSide(box: DOMRect, x: number, y: number, stacked = false): DropSide {
  const horizontal = stacked ? 0.5 : (x - box.left) / box.width;
  if (horizontal < 0.25) return 'before';
  if (horizontal > 0.75) return 'after';
  return y - box.top < box.height / 2 ? 'above' : 'below';
}
